import { Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import { HttpClient, HttpParams, HttpResponse } from '@angular/common/http';
import { BASE_PATH_API } from '@manager-base/app-base/environment/paths/constants';
import { WorkSessionDTO } from './dtos/work-session.dto';
import { StopWorkSessionDTO } from '@root/app/tasks/data/dtos/stop-work-session.dto';
import { UpdateWorkSessionDTO } from '@root/app/tasks/data/dtos/update-work-session.dto';
import { CreateTaskWorkSessionDTO } from '@root/app/tasks/data/dtos/create-task-work-session.dto';
import { WorkSessionSettingsDTO } from '@root/app/tasks/data/dtos/work-session-settings.dto';
import { WorkSessionBulkUpdateDTO } from '@root/app/tasks/data/dtos/work-session-bulk-update.dto';

@Injectable({
    providedIn: 'root',
})
export class WorkSessionsApiService {
    constructor(
        private http: HttpClient
    ) {
    }

    public getActiveWorkSession(): Observable<WorkSessionDTO> {
        const url = `${BASE_PATH_API}/worksessions/self`;
        return this.http.get<any>(url);
    }

    public getWorkSession(id: number, includeAttachments: boolean): Observable<WorkSessionDTO> {
        const url = `${BASE_PATH_API}/worksessions/${id}?includeAttachments=${includeAttachments}`;
        return this.http.get<any>(url);
    }

    public getWorkSessions(params: { TaskId: number, WorkOrderIds: number[] }): Observable<WorkSessionDTO[]> {
        let httpParams = new HttpParams();
        if (params.TaskId) {
            httpParams = httpParams.set('TaskId', params.TaskId.toString());
        }

        if (params.WorkOrderIds && params.WorkOrderIds.length > 0) {
            params.WorkOrderIds.forEach(id => {
                httpParams = httpParams.append('WorkOrderIds', id.toString());
            });
        }

        const url = `${BASE_PATH_API}/worksessions`;
        return this.http.get<WorkSessionDTO[]>(url, { params: httpParams });
    }

    public getWorkSessionCount(params: { CompletedByUserId: number, StartTime: string, EndTime: string, ExcludedWorkSessionId?: number }): Observable<number> {
        const url = `${BASE_PATH_API}/worksessions`;
        return this.http.head<number>(url, { params: new HttpParams({ fromObject: params }), observe: 'response' })
            .pipe(
                map((response: HttpResponse<any>) => {
                    const countHeader = response.headers.get('X-Buildium-WorkSession-Count');
                    return countHeader ? parseInt(countHeader, 10) : 0;
                })
            );
    }

    public stopWorkSession(stopWorkSessionDTO: StopWorkSessionDTO): Observable<WorkSessionDTO> {
        const url = `${BASE_PATH_API}/worksessions/self`;
        return this.http.put<any>(url, stopWorkSessionDTO);
    }

    public updateWorkSession(id: number, updateWorkSessionDTO: UpdateWorkSessionDTO): Observable<WorkSessionDTO> {
        const url = `${BASE_PATH_API}/worksessions/${id}`;
        return this.http.put<any>(url, updateWorkSessionDTO);
    }

    public createTaskWorkSession(createTaskWorkSessionDTO: CreateTaskWorkSessionDTO): Observable<WorkSessionDTO> {
        const url = `${BASE_PATH_API}/taskWorkSessions`;
        return this.http.post<WorkSessionDTO>(url, createTaskWorkSessionDTO);
    }

    public createWorkOrderWorkSession(createWorkOrderWorkSessionDTO: CreateTaskWorkSessionDTO): Observable<WorkSessionDTO> {
        const url = `${BASE_PATH_API}/workOrderWorkSessions`;
        return this.http.post<WorkSessionDTO>(url, createWorkOrderWorkSessionDTO);
    }

    public getWorkSessionSettings(): Observable<WorkSessionSettingsDTO> {
        const url = `${BASE_PATH_API}/worksessions/settings`;
        return this.http.get<WorkSessionSettingsDTO>(url);
    }

    public bulkUpdateWorkSession(workSessionBulkUpdateDTO: WorkSessionBulkUpdateDTO): Observable<void> {
        const url = `${BASE_PATH_API}/worksessions/bulk`;
        return this.http.put<void>(url, workSessionBulkUpdateDTO);
    }
}
